export interface BlogSection {
  id: string;
  title: string;
  content: string[];
  bullets?: string[];
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readingTime: string;
  category: string;
  image: string;
  author: { name: string; avatar: string };
  tags: string[];
  sections: BlogSection[];
}

export const posts: BlogPost[] = [
  {
    slug: 'quran-memorization-methods-for-busy-learners',
    title: "Qur'an Memorization Methods for Busy Learners",
    excerpt:
      'Build consistent hifz momentum with short daily cycles, spaced repetition, and intelligent review—suited for working adults and students.',
    date: '2025-08-20',
    readingTime: '7 min read',
    category: 'Hifz',
    image: '/quran-memorization.jpg',
    author: { name: 'Dhikr Academic Team', avatar: '/placeholder-user.jpg' },
    tags: ['Hifz', 'Consistency', 'Adults', 'Spaced Repetition'],
    sections: [
      {
        id: 'principles',
        title: 'Core Principles for Busy Schedules',
        content: [
          'Focus on short, high-frequency review windows rather than long, infrequent sessions. Your brain retains more with daily consistency.',
          'Anchor review to existing habits (e.g., after Fajr, commute, lunch). Consistency creates momentum and reduces friction.'
        ],
        bullets: [
          '5–10 minutes: past memorization (murājaʿah)',
          '10–15 minutes: new lines (sabaq)',
          '5 minutes: connector review (sabaq murājaʿah)'
        ]
      },
      {
        id: 'system',
        title: 'A Simple Weekly Hifz System',
        content: [
          'Design a loop that balances new memorization with cyclical review so you never drift too far from older passages.'
        ],
        bullets: [
          'Mon–Thu: new lines + connector review',
          'Fri: consolidation and audio self-check',
          'Sat: teacher feedback and accuracy correction',
          'Sun: light revision only'
        ]
      },
      {
        id: 'tools',
        title: 'Tools That Improve Retention',
        content: [
          'Use recitation recordings to compare makhārij and madd timing. Pair with spaced repetition cards for tricky ayāt.'
        ],
        bullets: [
          'Metronome-style pacing to stabilize flow',
          'Color-coded tajwīd prints or apps',
          'Progress tracker with small milestones'
        ]
      }
    ]
  },
  {
    slug: 'mastering-tajweed-from-foundation-to-fluency',
    title: 'Mastering Tajwīd: From Foundation to Fluency',
    excerpt:
      'Decode makhārij, ṣifāt and rule clusters with progressive drills and live correction. A roadmap to accurate recitation.',
    date: '2025-08-05',
    readingTime: '6 min read',
    category: 'Tajweed',
    image: '/quran-memorization.jpg',
    author: { name: 'Ustadhah Maryam', avatar: '/placeholder-user.jpg' },
    tags: ['Tajwid', 'Makhārij', 'Practice'],
    sections: [
      {
        id: 'foundations',
        title: 'Foundations First',
        content: [
          'Start with clear articulation points and consistent breath control. Mastery here reduces errors downstream in rules.'
        ]
      },
      {
        id: 'clusters',
        title: 'Learn Rules in Clusters',
        content: [
          'Group similar rules (e.g., nūn sākinah and tanwīn) so you can practice minimal pairs and hear contrasts.'
        ]
      },
      {
        id: 'feedback',
        title: 'Feedback Loop',
        content: [
          'Live correction + targeted drills = rapid refinement. Record and compare your own reading weekly.'
        ]
      }
    ]
  },
  {
    slug: 'learn-arabic-fast-practical-blueprint-for-beginners',
    title: 'Learn Arabic Fast: A Practical Blueprint for Beginners',
    excerpt:
      'Focus on high-frequency vocabulary, pattern recognition, and sentence frames to unlock real comprehension quickly.',
    date: '2025-07-12',
    readingTime: '8 min read',
    category: 'Arabic',
    image: '/quran-memorization.jpg',
    author: { name: 'Arabic Faculty', avatar: '/placeholder-user.jpg' },
    tags: ['Arabic', 'Vocabulary', 'Beginners'],
    sections: [
      {
        id: 'focus',
        title: 'What to Focus On',
        content: [
          'Prioritize high-frequency words and roots. Build sentence frames you can reuse across contexts.'
        ]
      },
      {
        id: 'listening',
        title: 'Listening and Patterning',
        content: [
          'Shadow short audio clips and decode morphological patterns in context to accelerate recognition.'
        ]
      },
      {
        id: 'practice',
        title: 'Daily Practice Rhythm',
        content: [
          'Short daily drills beat long weekly cramming. Keep it conversational and contextual.'
        ]
      }
    ]
  },
  {
    slug: 'islamic-studies-curriculum-parents-guide',
    title: "Islamic Studies Curriculum: A Parent's Guide",
    excerpt:
      'How ʿaqīdah, sīrah, fiqh and adab progress in age-appropriate layers—with reflection tasks that build character.',
    date: '2025-06-28',
    readingTime: '5 min read',
    category: 'Islamic Studies',
    image: '/quran-memorization.jpg',
    author: { name: 'Curriculum Team', avatar: '/placeholder-user.jpg' },
    tags: ['Islamic Studies', 'Parents', 'Youth'],
    sections: [
      {
        id: 'scope',
        title: 'Scope by Age',
        content: [
          'Introduce essentials early, deepen concepts with maturity, and tie learning to lived adab and reflection.'
        ]
      },
      {
        id: 'projects',
        title: 'Reflection Projects',
        content: [
          'Small family projects, journaling, and service build connection and retention beyond the lesson time.'
        ]
      }
    ]
  },
  {
    slug: 'kids-quran-online-keeping-young-learners-engaged',
    title: "Kids' Qur'an Online: Keeping Young Learners Engaged",
    excerpt:
      'Gamified repetition, bite-sized milestones and positive accountability help children enjoy recitation and memorization.',
    date: '2025-06-10',
    readingTime: '6 min read',
    category: 'Kids',
    image: '/quran-memorization.jpg',
    author: { name: 'Youth Programs', avatar: '/placeholder-user.jpg' },
    tags: ['Kids', 'Engagement', 'Hifz'],
    sections: [
      {
        id: 'engagement',
        title: 'Engagement Mechanics',
        content: [
          'Turn repetition into a game: points, milestones and visual trackers. Celebrate effort, not just outcomes.'
        ]
      },
      {
        id: 'parents',
        title: 'Parent-Teacher Partnership',
        content: [
          'Weekly notes with at-home tips keep progress steady and reduce frustration during busy weeks.'
        ]
      }
    ]
  },
  {
    slug: 'choose-the-right-online-quran-academy',
    title: "How to Choose the Right Online Qur'an Academy",
    excerpt:
      'Evaluate teacher credentials, feedback systems, schedule flexibility and trial lessons to ensure lasting results.',
    date: '2025-05-30',
    readingTime: '9 min read',
    category: 'Guides',
    image: '/quran-memorization.jpg',
    author: { name: 'Advisory Team', avatar: '/placeholder-user.jpg' },
    tags: ['Guides', 'Quality', 'Teachers'],
    sections: [
      {
        id: 'criteria',
        title: 'Key Criteria',
        content: [
          'Look for certified teachers, clear methodology, progress tracking and flexible scheduling with accountability.'
        ]
      },
      {
        id: 'trial',
        title: 'Use the Trial Lesson Wisely',
        content: [
          'Treat the trial as a diagnosis: assess communication, feedback clarity and structured next steps.'
        ]
      }
    ]
  }
];

export function getPostBySlug(slug: string) {
  return posts.find(p => p.slug === slug);
}
